import React, { useState, useCallback, useRef, useEffect, forwardRef } from "react";
import HTMLFlipBook from "react-pageflip";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTheme } from "@/components/ThemeProvider";
import { FloatingMenu } from "@/components/FloatingMenu";
import { getIcon } from "@/data/iconMap";
import { ChevronLeft, ChevronRight, CheckCircle2, ArrowRight } from "lucide-react";

const LOGO_URL = "https://supabase.dibantu.ai/storage/v1/object/public/catalog_indilabs/indilabs_logo_transparent.png";

const colorMap = {
  primary: {
    badge: "brand",
    iconBg: "bg-primary/10",
    iconText: "text-primary",
    checkColor: "text-primary",
    numberColor: "text-primary/15",
    glowClass: "from-primary/8 to-transparent",
  },
  secondary: {
    badge: "brand-green",
    iconBg: "bg-secondary/10",
    iconText: "text-secondary",
    checkColor: "text-secondary",
    numberColor: "text-secondary/15",
    glowClass: "from-secondary/8 to-transparent",
  },
  accent: {
    badge: "brand-accent",
    iconBg: "bg-accent/10",
    iconText: "text-accent",
    checkColor: "text-accent",
    numberColor: "text-accent/15",
    glowClass: "from-accent/8 to-transparent",
  },
};

/* ---- COVER PAGE (LEFT - with branding + word cloud) ---- */
const CoverPage = forwardRef(function CoverPage({ theme }, ref) {
  const wordcloudSrc = theme === 'dark'
    ? 'https://supabase.dibantu.ai/storage/v1/object/public/catalog_indilabs/wordcloud_dark.png'
    : 'https://supabase.dibantu.ai/storage/v1/object/public/catalog_indilabs/wordcloud_light.png';

  return (
    <div ref={ref} className="page-content bg-card relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: `linear-gradient(hsl(var(--primary) / 0.4) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary) / 0.4) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }}
      />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/3 w-[400px] h-[400px] rounded-full bg-gradient-radial from-primary/8 to-transparent blur-3xl" />

          <div className="relative z-10 flex flex-col h-full">
            {/* Top: Logo */}
            <div className="px-6 pt-6 md:px-8 md:pt-8 flex flex-col items-center">
              <img
                src={LOGO_URL}
                alt="Indilabs"
                className="h-20 md:h-24 w-auto object-contain max-w-[60vw] md:max-w-[220px] transition-all duration-300 mb-2 md:mb-4"
                style={{ minHeight: 48 }}
              />
              {/* Middle: Title */}
              <div className="flex flex-col items-center justify-start px-2 md:px-6 text-center mt-1 md:mt-2">
                <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight mb-0.5 md:mb-1">
                  Product Showcase
                </h1>
                {/* <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold leading-tight mb-2 md:mb-3 bg-gradient-to-r from-[#2563eb] via-[#16a34a] to-[#22c55e] bg-clip-text text-transparent">
                  Showcase
                </h1> */}
                <p className="text-xs md:text-sm text-muted-foreground leading-relaxed max-w-xs mb-0.5 md:mb-1">
                  Explore our portfolio of AI-driven solutions
                </p>
              </div>

              {/* Word cloud image */}
              <div className="mt-3 md:mt-5 w-full flex-1 flex items-center justify-center">
                <div className="rounded-xl overflow-hidden">
                  <img src={wordcloudSrc} alt="Word cloud" className="w-full h-auto object-contain" />
                </div>
              </div>

            </div>

        {/* Footer - stick to bottom */}
        <div className="px-6 pb-4 md:px-8 md:pb-5 mt-auto">
          <p className="text-[10px] text-muted-foreground/40 text-center">
            © {new Date().getFullYear()} Innovader Digital Labs. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
});

/* ---- TOC PAGE (RIGHT - Table of Contents matching mockup) ---- */
const TocPage = forwardRef(function TocPage({ showcases }, ref) {
  return (
    <div ref={ref} className="page-content bg-card relative overflow-hidden">
      <div className="relative z-10 flex flex-col h-full px-6 md:px-10 py-8 md:py-10">
        <p className="text-[10px] md:text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground text-center mb-6 md:mb-8">
          Table of Contents
        </p>

        <div className="flex-1 flex flex-col justify-center">
          <div className="space-y-1.5 md:space-y-2">
            {showcases.map((showcase, index) => {
              const Icon = getIcon(showcase.icon);
              const num = String(index + 1).padStart(2, '0');
              return (
                <div
                  key={showcase.id}
                  className="flex items-center gap-3 md:gap-4 px-3 md:px-4 py-2.5 md:py-3 rounded-lg hover:bg-muted/30 transition-colors duration-200 group"
                >
                  <span className="flex items-center justify-center w-7 h-7 md:w-8 md:h-8 rounded-lg bg-primary/10 text-primary text-[10px] md:text-xs font-heading font-bold shrink-0">
                    {num}
                  </span>
                  <p className="flex-1 text-xs md:text-sm font-medium font-heading text-foreground truncate">
                    {showcase.title}
                  </p>
                  <Icon className="h-4 w-4 text-muted-foreground/40 group-hover:text-muted-foreground/70 shrink-0 transition-colors duration-200" />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
});

/* ---- PRODUCT CONTENT PAGE (LEFT) ---- */
const ProductContentPage = forwardRef(function ProductContentPage({ showcase, index }, ref) {
  const colors = colorMap[showcase.color] || colorMap.primary;
  const pageNumber = String(index + 1).padStart(2, '0');

  return (
    <div ref={ref} className="page-content bg-card flex flex-col relative overflow-hidden h-full">
      <div className={cn("absolute top-0 right-0 w-[300px] h-[300px] rounded-full bg-gradient-radial blur-3xl opacity-40 pointer-events-none", colors.glowClass)} />

      <div className="relative z-10 flex flex-col h-full px-8 md:px-10 lg:px-14 py-8 md:py-10 lg:py-14">
        <div className="flex items-center gap-4 mb-5 md:mb-7">
          <span className={cn("font-heading text-5xl md:text-6xl lg:text-7xl font-bold leading-none", colors.numberColor)}>
            {pageNumber}
          </span>
          <Badge variant={colors.badge} className="text-[10px] md:text-xs">
            {showcase.category || `Showcase ${pageNumber}`}
          </Badge>
        </div>

        <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-foreground leading-tight mb-3 md:mb-4">
          {showcase.title}
        </h2>

        <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-6 md:mb-8">
          {showcase.subtitle}
        </p>

        <div className="space-y-2.5 md:space-y-3 flex-1">
          {(showcase.advantages || []).map((adv, i) => (
            <div
              key={i}
              className="bg-muted/40 border border-border/20 rounded-lg p-3 md:p-4 flex items-start gap-3 transition-colors duration-200 hover:border-border/40"
            >
              <CheckCircle2 className={cn("h-4 w-4 md:h-5 md:w-5 mt-0.5 shrink-0", colors.checkColor)} />
              <div>
                <p className="text-sm md:text-base font-medium text-foreground font-heading leading-snug">{adv.title}</p>
                <p className="text-xs md:text-sm text-muted-foreground mt-0.5 leading-relaxed">{adv.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-5 md:mt-7 mb-4 md:mb-0">
          {showcase.learnMoreUrl && showcase.learnMoreUrl !== "#" ? (
            <a href={showcase.learnMoreUrl} target="_blank" rel="noopener noreferrer">
              <Button variant="brand" size="default" className="group font-heading text-sm">
                Learn More
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Button>
            </a>
          ) : (
            <Button variant="brand" size="default" className="group font-heading text-sm">
              Learn More
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
});

/* ---- PRODUCT IMAGE PAGE (RIGHT) ---- */
const ProductImagePage = forwardRef(function ProductImagePage({ showcase, index, onFlipNext }, ref) {
  const Icon = getIcon(showcase.icon);
  const colors = colorMap[showcase.color] || colorMap.primary;
  const pageNumber = String(index + 1).padStart(2, '0');

  return (
    <div ref={ref} className="page-content bg-card relative overflow-hidden h-full">
      <img
        src={showcase.image}
        alt={showcase.title}
        className="absolute inset-0 w-full h-full object-fill"
        loading="eager"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-background/20 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-background/30 to-transparent" />

      <div className="absolute top-6 right-6 z-10">
        <div className="px-4 py-3 rounded-xl backdrop-blur-xl bg-card/50 border border-border/20">
          <p className="text-[10px] text-muted-foreground/70 uppercase tracking-wider font-heading">{showcase.category || 'Showcase'}</p>
          <p className={cn("text-xl md:text-2xl font-heading font-bold", colors.iconText)}>{pageNumber}</p>
        </div>
      </div>

      <div className="absolute bottom-14 md:bottom-6 left-6 right-6 z-10">
        <div
          className="flex items-center gap-4 px-5 py-4 rounded-xl backdrop-blur-xl bg-card/50 border border-border/20 cursor-pointer hover:bg-card/70 active:bg-card/80 transition-colors duration-200"
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); onFlipNext(); }}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); onFlipNext(); }}
          onPointerDown={(e) => e.stopPropagation()}
          role="button"
          tabIndex={0}
        >
          <div className={cn("flex items-center justify-center w-12 h-12 rounded-xl", colors.iconBg)}>
            <Icon className={cn("h-6 w-6", colors.iconText)} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-base font-heading font-semibold text-foreground truncate">{showcase.title}</p>
            <p className="text-xs text-muted-foreground">{(showcase.advantages || []).length} key features</p>
          </div>
          <Badge variant={colors.badge} className="text-[10px] shrink-0">
            Active
          </Badge>
        </div>
      </div>
    </div>
  );
});

/* ---- BACK COVER PAGE ---- */
const BackCoverPage = forwardRef(function BackCoverPage(props, ref) {
  return (
    <div ref={ref} className="page-content bg-card relative overflow-hidden">
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `linear-gradient(hsl(var(--secondary) / 0.4) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--secondary) / 0.4) 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}
        />
        <div className="absolute bottom-1/3 left-1/2 -translate-x-1/2 translate-y-1/2 w-[400px] h-[400px] rounded-full bg-gradient-radial from-secondary/8 to-transparent blur-3xl" />

        <div className="relative z-10 text-center px-10 flex flex-col items-center">
          <div className="bg-muted/60 backdrop-blur-sm rounded-2xl p-4 border border-border/30 mb-8">
            <img src={LOGO_URL} alt="Indilabs" className="h-12 md:h-16 w-auto object-contain" />
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground leading-tight mb-4">
            Let's Build
            <span className="block bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Together
            </span>
          </h2>
          <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-7 max-w-sm">
            Ready to transform your operations with AI? Contact us to explore how our solutions can work for you.
          </p>
          <a
            href="https://api.whatsapp.com/send/?phone=6285117517521&text=Halo%2C+saya+tertarik+dengan+layanan+Indilabs&type=phone_number&app_absent=0"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="brand" size="default" className="font-heading text-sm">
              Get in Touch
              <ArrowRight className="h-4 w-4" />
            </Button>
          </a>
          <p className="text-xs text-muted-foreground/40 tracking-[0.2em] uppercase font-heading mt-10">
            indilabs.co.id
          </p>
        </div>
      </div>
    </div>
  );
});

/* ---- MAIN FLIPBOOK COMPONENT ---- */
export const FlipBook = ({ showcases = [], categories = [] }) => {
  const bookRef = useRef(null);
  const { theme } = useTheme();
  const [currentPage, setCurrentPage] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dimensions, setDimensions] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [bookKey, setBookKey] = useState(0);

  // Group showcases by category in the order defined by categories array
  const groupedData = React.useMemo(() => {
    if (!categories.length) {
      // Fallback: derive categories from showcases
      const seen = [];
      showcases.forEach(s => {
        if (s.category && !seen.find(c => c.key === s.category)) {
          seen.push({ key: s.category, label: String.fromCharCode(65 + seen.length), title: s.category, color: s.color || 'primary' });
        }
      });
      return seen.map(cat => ({
        category: cat,
        items: showcases.filter(s => s.category === cat.key)
      }));
    }
    return categories.map(cat => ({
      category: cat,
      items: showcases.filter(s => s.category === cat.key)
    }));
  }, [showcases, categories]);

  // Build page-to-showcase index mapping for navigation
  // Page structure: CoverPage(1) + TocPage(1) + [items*2 per category] + BackCoverPage(1)
  const showcasePageMap = React.useMemo(() => {
    const map = {}; // showcaseIdx → page number
    let pageOffset = 2; // starts after cover + toc
    let flatIdx = 0;
    groupedData.forEach(({ items }) => {
      items.forEach(() => {
        map[flatIdx] = pageOffset;
        pageOffset += 2; // image + content pages
        flatIdx++;
      });
    });
    return map;
  }, [groupedData]);

  useEffect(() => {
    const updateDimensions = () => {
      const vw = window.innerWidth;
      const vh = window.visualViewport?.height || window.innerHeight;
      const mobile = vw < 768;
      setIsMobile(mobile);

      const newDims = mobile
        ? { width: vw, height: vh }
        : { width: Math.floor(vw / 2), height: vh };

      setDimensions(prev => {
        if (!prev) {
          // First measurement: force remount so HTMLFlipBook uses correct size
          setTimeout(() => setBookKey(k => k + 1), 0);
        }
        return newDims;
      });
    };

    // Force remount flipbook after fullscreen change so it picks up new dimensions
    const handleFullscreenChange = () => {
      updateDimensions();
      setBookKey(k => k + 1);
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    window.visualViewport?.addEventListener('resize', updateDimensions);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      window.removeEventListener('resize', updateDimensions);
      window.visualViewport?.removeEventListener('resize', updateDimensions);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  const onFlip = useCallback((e) => {
    setCurrentPage(e.data);
  }, []);

  const onInit = useCallback(() => { }, []);

  const flipNext = useCallback(() => {
    if (bookRef.current) bookRef.current.pageFlip().flipNext();
  }, []);

  const flipPrev = useCallback(() => {
    if (bookRef.current) bookRef.current.pageFlip().flipPrev();
  }, []);

  const goToShowcase = useCallback((showcaseIdx) => {
    if (bookRef.current) {
      const targetPage = showcasePageMap[showcaseIdx];
      if (targetPage !== undefined) {
        bookRef.current.pageFlip().turnToPage(targetPage);
      }
      setMenuOpen(false);
    }
  }, [showcasePageMap]);

  // Toggle browser fullscreen (called from user click = valid gesture)
  const handleToggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => { });
    } else {
      document.exitFullscreen().catch(() => { });
    }
  }, []);

  // Simple refresh like F5
  const handleRefresh = useCallback(() => {
    window.location.reload();
  }, []);

  const getCurrentShowcaseIdx = () => {
    if (currentPage === 0) return -1;
    // Find the showcase whose page range contains currentPage
    for (const [idx, page] of Object.entries(showcasePageMap)) {
      if (currentPage >= page && currentPage <= page + 1) {
        return parseInt(idx, 10);
      }
    }
    return -1;
  };

  const currentShowcaseIdx = getCurrentShowcaseIdx();

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (menuOpen) return;
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        flipNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        flipPrev();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [flipNext, flipPrev, menuOpen]);

  return (
    <div className="relative w-full h-[100dvh] overflow-hidden bg-background">
      {/* Navigation Arrow - LEFT CENTER */}
      <button
        onClick={flipPrev}
        className="fixed left-3 md:left-5 top-1/2 -translate-y-1/2 z-30 group"
        aria-label="Previous page"
      >
        <div className="flex items-center justify-center w-10 h-10 md:w-11 md:h-11 rounded-full bg-card/70 border border-border/30 backdrop-blur-md transition-all duration-200 group-hover:bg-primary/15 group-hover:border-primary/30 group-hover:shadow-glow-blue">
          <ChevronLeft className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors duration-200" />
        </div>
      </button>

      {/* Navigation Arrow - RIGHT CENTER */}
      <button
        onClick={flipNext}
        className="fixed right-3 md:right-5 top-1/2 -translate-y-1/2 z-30 group"
        aria-label="Next page"
      >
        <div className="flex items-center justify-center w-10 h-10 md:w-11 md:h-11 rounded-full bg-card/70 border border-border/30 backdrop-blur-md transition-all duration-200 group-hover:bg-primary/15 group-hover:border-primary/30 group-hover:shadow-glow-blue">
          <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors duration-200" />
        </div>
      </button>

      {/* Floating Menu */}
      <FloatingMenu
        currentShowcaseIdx={currentShowcaseIdx}
        showcases={showcases}
        groupedData={groupedData}
        onGoToShowcase={goToShowcase}
        onRefresh={handleRefresh}
        onToggleFullscreen={handleToggleFullscreen}
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
      />

      {/* THE FULLSCREEN BOOK */}
      <div className="w-full h-full flex items-center justify-center">
        {dimensions && (
          <HTMLFlipBook
            key={bookKey}
            ref={bookRef}
            width={dimensions.width}
            height={dimensions.height}
            size="fixed"
            minWidth={280}
            maxWidth={3000}
            minHeight={400}
            maxHeight={2000}
            showCover={false}
            maxShadowOpacity={theme === "dark" ? 0.7 : 0.25}
            drawShadow={true}
            flippingTime={800}
            usePortrait={isMobile}
            startZIndex={0}
            autoSize={false}
            clickEventForward={true}
            useMouseEvents={true}
            swipeDistance={30}
            showPageCorners={false}
            disableFlipByClick={false}
            onFlip={onFlip}
            onInit={onInit}
            className="book-fullscreen"
            style={{}}
            startPage={0}
            mobileScrollSupport={false}
          >
            <CoverPage theme={theme} />
            <TocPage showcases={showcases} />
            {groupedData.flatMap(({ category, items }) => {
              let flatStartIdx = 0;
              for (const g of groupedData) {
                if (g.category.key === category.key) break;
                flatStartIdx += g.items.length;
              }
              return items.flatMap((showcase, localIdx) => {
                const globalIdx = flatStartIdx + localIdx;
                return [
                  <ProductImagePage key={`image-${globalIdx}`} showcase={showcase} index={globalIdx} onFlipNext={flipNext} />,
                  <ProductContentPage key={`content-${globalIdx}`} showcase={showcase} index={globalIdx} />,
                ];
              });
            })}
            <BackCoverPage />
          </HTMLFlipBook>
        )}
      </div>
    </div>
  );
};
